import React from 'react'
import { useMutation } from '@apollo/client'
import { formatISO, parseISO } from 'date-fns'
import Button from 'components/Button'
import {
  Gender,
  Ethnicity,
  Race,
  getErrorMessage,
  UserFieldName,
  useUserForm,
  UserFormValues,
  User_user,
  getUserGqlCompliant,
  EditUser,
  EditUserVariables,
  AddressInput as AddressInputType,
} from '@modmd/data'
import Card from 'components/Card'
import { Box } from 'components/Layout'
import { SelectInput } from 'components/SelectInput'
import TextInput from 'components/TextInput'
import { DateInput } from 'components/DateInput'
import { AddressInput } from 'components/AddressInput'
import { userInvitePending, maskPhone } from 'utils/helpers'
import { useToaster } from 'utils/useToaster'
import { FULL_USER } from 'sharedOperations/user/operations'
import { EDIT_USER } from './operations'

interface UserTestProps {
  refetch?: () => void
  onDismiss: () => void
  data: User_user | null | undefined
  notverifyUserInvitePending?: boolean
}

const EditPersonal = ({
  refetch,
  onDismiss,
  data,
  notverifyUserInvitePending = false,
}: UserTestProps) => {
  const initialUserData = data
  const { setToastMessage } = useToaster()

  const [editUser, { loading: isEditLoading }] = useMutation<EditUser, EditUserVariables>(
    EDIT_USER,
    {
      onCompleted: () => {
        setToastMessage('Data successfully changed', 'success')
      },
      refetchQueries: [
        {
          query: FULL_USER,
          variables: {
            id: data?.id?.toString() ?? '',
          },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const userForm = useUserForm({
    validationSchema: initialUserData?.parentId ? 'subpersonUser' : 'fullUser',
    onSubmit: (values) => {
      void editUser({
        variables: {
          inputData: getUserGqlCompliant(values, [
            UserFieldName.ID,
            UserFieldName.FIRSTNAME,
            UserFieldName.LASTNAME,
            UserFieldName.BIRTHDATE,
            UserFieldName.GENDER,
            UserFieldName.PHONE_NUMBER,
            UserFieldName.ADDRESS,
            UserFieldName.ETHNICITY,
            UserFieldName.RACE,
          ]),
        },
      })
      if (refetch) refetch()
      onDismiss()
    },
  })

  React.useEffect(() => {
    void userForm.setValues({
      ...data,
    } as UserFormValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleAddressChange = React.useCallback(
    (addressInput: Partial<AddressInputType>) => {
      void userForm.setFieldValue('Address', { ...addressInput })
    },
    [userForm]
  )

  return (
    <Card.Content display="grid" gridGap="1rem">
      <Box>
        <TextInput
          name={UserFieldName.FIRSTNAME}
          value={userForm.values.firstName || ''}
          label="Firstname"
          onChange={userForm.handleChange}
          onBlur={userForm.handleBlur}
          isFullWidth
          errorMessage={getErrorMessage(userForm.touched.firstName, userForm.errors.firstName)}
        />
        <TextInput
          name={UserFieldName.LASTNAME}
          value={userForm.values.lastName || ''}
          label="Lastname"
          onChange={userForm.handleChange}
          onBlur={userForm.handleBlur}
          isFullWidth
          errorMessage={getErrorMessage(userForm.touched.lastName, userForm.errors.lastName)}
        />
        <TextInput
          name={UserFieldName.PHONE_NUMBER}
          value={maskPhone(userForm.values.phoneNumber as string) || ''}
          label="Phone number"
          onChange={userForm.handleChange}
          onBlur={userForm.handleBlur}
          isFullWidth
          errorMessage={getErrorMessage(userForm.touched.phoneNumber, userForm.errors.phoneNumber)}
        />
        <DateInput
          id="birthdate"
          name={UserFieldName.BIRTHDATE}
          label="Birthdate"
          onBlur={userForm.handleBlur}
          value={userForm.values.birthDate ? parseISO(userForm.values.birthDate) : null}
          onChange={(date) => {
            void userForm.setFieldValue('birthDate', date ? formatISO(date) : null)
          }}
          errorMessage={getErrorMessage(userForm.touched.birthDate, userForm.errors.birthDate)}
          isFullWidth
        />
        <SelectInput
          name={UserFieldName.GENDER}
          label="Gender"
          onChange={userForm.handleChange}
          value={userForm.values.gender || ''}
          hasEmptyValue
          options={[
            { value: '', label: 'Choose one', disabled: true },
            { value: Gender.Male, label: 'Male' },
            {
              value: Gender.Female,
              label: 'Female',
            },
            {
              value: Gender.Other,
              label: 'Other',
            },
            {
              value: Gender.DeclineToState,
              label: 'Decline to state',
            },
          ]}
          errorMessage={getErrorMessage(userForm.touched.gender, userForm.errors.gender)}
        />
        <SelectInput
          name={UserFieldName.ETHNICITY}
          label="Ethnicity"
          hasEmptyValue
          onChange={userForm.handleChange}
          value={userForm.values.ethnicity || ''}
          options={[
            { value: '', label: 'Choose one', disabled: true },
            { value: Ethnicity.Hispanic, label: 'Hispanic' },
            { value: Ethnicity.NotHispanic, label: 'Not-Hispanic' },
            { value: Ethnicity.DeclineToState, label: 'Decline to state' },
          ]}
          errorMessage={getErrorMessage(userForm.touched.ethnicity, userForm.errors.ethnicity)}
        />
        <SelectInput
          name={UserFieldName.RACE}
          label="Race"
          hasEmptyValue
          onChange={userForm.handleChange}
          value={userForm.values.race || ''}
          options={[
            { value: '', label: 'Choose one', disabled: true },
            { value: Race.AmericanIndian, label: 'American Indian/Alaska Native' },
            { value: Race.Asian, label: 'Asian' },
            { value: Race.Black, label: 'Black' },
            { value: Race.Hawaiian, label: 'Hawaiian/Pacific Islander' },
            { value: Race.White, label: 'White' },
            { value: Race.Other, label: 'Other' },
            { value: Race.DeclineToState, label: 'Decline to State' },
          ]}
          errorMessage={getErrorMessage(userForm.touched.race, userForm.errors.race)}
        />
        {(notverifyUserInvitePending ||
          userInvitePending(initialUserData) ||
          !userForm.values.Address) && (
          <AddressInput
            onTouched={(touched) => userForm.setTouched({ ...userForm.touched, Address: touched })}
            errors={userForm.errors.Address}
            touched={userForm.touched.Address}
            value={userForm.values.Address}
            onChange={handleAddressChange}
          />
        )}
        <Button isFetching={isEditLoading} onClick={() => userForm.handleSubmit()}>
          Submit data
        </Button>
      </Box>
    </Card.Content>
  )
}

export { EditPersonal }
